import { buildRegistry } from '@cards/engine';
import { defineDeckPassive } from './define-deck-passive.js';
import { isPixelGlyph } from './pixel-glyph.js';
import { normalizeGameCardUi } from './ui/normalize.js';
function assertNonEmpty(value, what) {
    if (value.length === 0)
        throw new Error(`${what} должен быть непустой строкой`);
}
function assertRecord(value, what) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        throw new Error(`${what} должен быть объектом`);
    }
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
        throw new Error(`${what} должен быть обычным объектом`);
    }
}
function assertOnlyKeys(value, allowed, what) {
    for (const key of Reflect.ownKeys(value)) {
        if (typeof key !== 'string' || !allowed.has(key)) {
            throw new Error(`${what} содержит неизвестное поле "${String(key)}"`);
        }
    }
}
function assertDeckShape(deck) {
    assertNonEmpty(deck.id, 'defineDeck: id');
    for (const [key, value] of Object.entries({
        name: deck.name,
        description: deck.description,
        strength: deck.strength,
        weakness: deck.weakness,
    })) {
        assertNonEmpty(value, `defineDeck "${deck.id}": ${key}`);
    }
    if (!['available', 'coming_soon'].includes(deck.availability)) {
        throw new Error(`defineDeck "${deck.id}": неизвестная availability "${String(deck.availability)}"`);
    }
    if (deck.cardIds.length !== 12) {
        throw new Error(`defineDeck "${deck.id}" должен содержать ровно 12 CardDefId`);
    }
    if (new Set(deck.cardIds).size !== deck.cardIds.length) {
        throw new Error(`defineDeck "${deck.id}" должен содержать 12 уникальных CardDefId`);
    }
    if (deck.cardIds.some((id) => typeof id !== 'string' || id.length === 0)) {
        throw new Error(`defineDeck "${deck.id}" содержит пустой CardDefId`);
    }
}
export function defineDeck(input) {
    const deck = {
        id: input.id,
        name: input.name,
        description: input.description,
        strength: input.strength,
        weakness: input.weakness,
        availability: input.availability,
        cardIds: input.cards.map((card) => card.id),
    };
    assertDeckShape(deck);
    return Object.freeze({ ...deck, cardIds: Object.freeze([...deck.cardIds]) });
}
function validateCard(card) {
    if (card.id !== card.definition.id || card.id !== card.presentation.id) {
        throw new Error(`buildContent: ids definition/presentation не совпадают для "${card.id}"`);
    }
    if (!isPixelGlyph(card.presentation.glyph)) {
        throw new Error(`buildContent: card "${card.id}" содержит некорректный glyph`);
    }
    normalizeGameCardUi(card.ui, `buildContent card "${card.id}"`);
}
function validateDeckPassive(passive) {
    assertRecord(passive, 'buildContent: deck passive');
    const owner = `buildContent: passive "${String(passive.id)}"`;
    assertOnlyKeys(passive, new Set(['id', 'definition', 'presentation', 'role', 'initialEffect', 'source']), owner);
    assertRecord(passive.definition, `${owner}.definition`);
    assertOnlyKeys(passive.definition, new Set(['id', 'type', 'hooks']), `${owner}.definition`);
    assertRecord(passive.presentation, `${owner}.presentation`);
    assertOnlyKeys(passive.presentation, new Set(['id', 'name', 'description', 'glyph']), `${owner}.presentation`);
    assertRecord(passive.initialEffect, `${owner}.initialEffect`);
    assertOnlyKeys(passive.initialEffect, new Set(['vars', 'statMods']), `${owner}.initialEffect`);
    if (!Object.hasOwn(passive.initialEffect, 'vars')) {
        throw new Error(`${owner}.initialEffect.vars обязателен`);
    }
    if (passive.id !== passive.definition.id || passive.id !== passive.presentation.id
        || passive.definition.type !== 'EFFECT' || passive.source !== 'deck_passive') {
        throw new Error(`buildContent: passive ids/type/source не совпадают для "${passive.id}"`);
    }
    if (passive.role !== 'available' && passive.role !== 'legacy') {
        throw new Error(`buildContent: passive "${passive.id}" имеет неизвестную role`);
    }
    // Re-run the public authoring boundary so forged objects cannot bypass glyph,
    // hooks, JSON vars or statMods validation merely by satisfying TypeScript.
    defineDeckPassive({
        id: passive.id,
        role: passive.role,
        presentation: {
            name: passive.presentation.name,
            description: passive.presentation.description,
            glyph: passive.presentation.glyph,
        },
        initialEffect: passive.initialEffect,
        ...(passive.definition.hooks === undefined ? {} : { hooks: passive.definition.hooks }),
    });
}
export function buildContent(input) {
    const cards = new Map();
    for (const card of input.cards) {
        validateCard(card);
        if (cards.has(card.id))
            throw new Error(`buildContent: дубликат card id "${card.id}"`);
        cards.set(card.id, card);
    }
    const deckPassives = new Map();
    for (const passive of input.deckPassives ?? []) {
        validateDeckPassive(passive);
        if (deckPassives.has(passive.id)) {
            throw new Error(`buildContent: дубликат deck passive id "${passive.id}"`);
        }
        deckPassives.set(passive.id, passive);
    }
    const glyphOwners = new Map();
    for (const card of cards.values()) {
        const glyph = JSON.stringify(card.presentation.glyph);
        const previous = glyphOwners.get(glyph);
        if (previous !== undefined) {
            throw new Error(`buildContent: cards "${previous}" и "${card.id}" используют одинаковый glyph`);
        }
        glyphOwners.set(glyph, card.id);
    }
    for (const passive of deckPassives.values()) {
        const glyph = JSON.stringify(passive.presentation.glyph);
        const previous = glyphOwners.get(glyph);
        if (previous !== undefined) {
            throw new Error(`buildContent: definitions "${previous}" и "${passive.id}" используют одинаковый glyph`);
        }
        glyphOwners.set(glyph, passive.id);
    }
    for (const card of cards.values()) {
        for (const dependency of card.dependencies) {
            if (!cards.has(dependency.id)) {
                throw new Error(`buildContent: card "${card.id}" зависит от отсутствующей "${dependency.id}"`);
            }
        }
    }
    const deckIds = new Set();
    const decksById = new Map();
    for (const deck of input.decks) {
        assertDeckShape(deck);
        if (deckIds.has(deck.id))
            throw new Error(`buildContent: дубликат deck id "${deck.id}"`);
        deckIds.add(deck.id);
        decksById.set(deck.id, deck);
        for (const cardId of deck.cardIds) {
            const card = cards.get(cardId);
            if (card === undefined) {
                throw new Error(`buildContent: deck "${deck.id}" содержит неизвестную card "${cardId}"`);
            }
            if (card.role !== 'playable') {
                throw new Error(`buildContent: deck "${deck.id}" содержит card "${cardId}" с ролью "${card.role}"`);
            }
        }
    }
    return Object.freeze({
        registry: buildRegistry([
            ...[...cards.values()].map((card) => card.definition),
            ...[...deckPassives.values()].map((passive) => passive.definition),
        ]),
        catalog: new Map([...cards].map(([id, card]) => [id, card.presentation])),
        decks: Object.freeze([...input.decks]),
        decksById,
        ui: new Map([...cards].map(([id, card]) => [id, card.ui])),
        cards,
        deckPassives,
        deckPassiveCatalog: new Map([...deckPassives].map(([id, passive]) => [id, passive.presentation])),
    });
}
//# sourceMappingURL=build-content.js.map